import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Button,
    TextField,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack
} from '@mui/material';
import {
    DragIndicator as DragIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    LibraryAdd as LibraryAddIcon,
    Delete as DeleteIcon,
    Event as EventIcon
} from '@mui/icons-material';
import { useSessionStudio } from './SessionContext';
import { ProblemGroupWithProblems } from 'services/sessionService';
import ProblemItem from './ProblemItem';
import ProblemPickerDialog from './ProblemPickerDialog';

import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';


interface GroupCardProps {
    group: ProblemGroupWithProblems;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const SessionGroupCard: React.FC<GroupCardProps> = ({ group, dragHandleProps }) => {
    const [expanded, setExpanded] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState(group.title);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const { addProblem, updateProblemGroup, selectedSessionId, refreshSession, deleteProblemGroup, isSaving } = useSessionStudio();

    // Date inputs use yyyy-MM-dd; stored values are ISO strings.
    const toDateInput = (value?: string | null) => (value ? value.substring(0, 10) : '');
    const [startDate, setStartDate] = useState(toDateInput(group.startDate));
    const [endDate, setEndDate] = useState(toDateInput(group.endDate));

    const handleSaveTitle = async () => {
        if (title !== group.title) {
            try {
                await updateProblemGroup(group.id, { title });
            } catch (error) {
                console.error('Failed to update group title:', error);
                setTitle(group.title); // Revert on error
            }
        }
        setIsEditing(false);
    };

    const handleAddProblem = async () => {
        try {
            await addProblem(group.id);
        } catch (error) {
            console.error('Failed to add problem:', error);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleDeleteGroup = async () => {
        handleMenuClose();
        if (deleteProblemGroup) {
            try {
                await deleteProblemGroup(group.id);
            } catch (error) {
                console.error('Failed to delete group:', error);
            }
        }
    };

    const handleAddFromPool = () => {
        handleMenuClose();
        setPickerOpen(true);
    };

    const handleOpenSchedule = () => {
        handleMenuClose();
        setStartDate(toDateInput(group.startDate));
        setEndDate(toDateInput(group.endDate));
        setScheduleOpen(true);
    };

    const handleSaveSchedule = async () => {
        try {
            await updateProblemGroup(group.id, {
                // Empty input clears the date (null = visible immediately / no end).
                startDate: startDate ? new Date(startDate).toISOString() : undefined,
                endDate: endDate ? new Date(endDate).toISOString() : undefined
            });
            setScheduleOpen(false);
        } catch (error) {
            console.error('Failed to update schedule:', error);
        }
    };

    // Caption describing when this group becomes visible to students.
    const visibilityLabel = group.startDate
        ? `Visible from ${new Date(group.startDate).toLocaleDateString()}`
        : 'Visible immediately';

    const handlePickerSuccess = () => {
        refreshSession();
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
            }}
        >
            {/* Group Header */}
            <Box sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderBottom: expanded ? '1px solid' : 'none',
                borderColor: 'divider'
            }}>
                <Box {...dragHandleProps} sx={{ display: 'flex', alignItems: 'center', mr: 1, cursor: 'grab' }}>
                    <IconButton size="small" tabIndex={-1}>
                        <DragIcon fontSize="small" color="action" />
                    </IconButton>
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Week {group.groupOrder || '?'}
                        </Typography>
                        {isEditing ? (
                            <TextField
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onBlur={handleSaveTitle}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveTitle();
                                    if (e.key === 'Escape') {
                                        setTitle(group.title);
                                        setIsEditing(false);
                                    }
                                }}
                                size="small"
                                variant="standard"
                                autoFocus
                                sx={{ flexGrow: 1, maxWidth: 300 }}
                            />
                        ) : (
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                onClick={() => setIsEditing(true)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                — {group.title}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {group.problems.length} Problems • {visibilityLabel}
                        {group.endDate && ` • Closes ${new Date(group.endDate).toLocaleDateString()}`}
                    </Typography>
                </Box>

                <Box>
                    <IconButton size="small" onClick={handleMenuOpen}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={handleAddFromPool}>
                            <ListItemIcon>
                                <LibraryAddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Add from Pool</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenSchedule}>
                            <ListItemIcon>
                                <EventIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit Schedule</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleDeleteGroup} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>Delete Group</ListItemText>
                        </MenuItem>
                    </Menu>
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>

            {/* Problems List */}
            <Collapse in={expanded}>
                <Box sx={{ bgcolor: 'background.default' }}>
                    {group.problems.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No problems in this group.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Button size="small" startIcon={<AddIcon />} onClick={handleAddProblem} disabled={isSaving}>
                                    Add Problem
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<LibraryAddIcon />}
                                    variant="outlined"
                                    onClick={() => setPickerOpen(true)}
                                    disabled={isSaving}
                                >
                                    Add from Pool
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            {group.problems.map((problem, index) => (
                                <ProblemItem
                                    key={problem.id}
                                    problem={problem}
                                    isLast={index === group.problems.length - 1}
                                />
                            ))}
                            {/* Add Problem Row */}
                            <Box sx={{ p: 1, pl: 6, display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    color="inherit"
                                    sx={{ color: 'text.secondary' }}
                                    onClick={handleAddProblem}
                                    disabled={isSaving}
                                >
                                    Add Problem
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<LibraryAddIcon />}
                                    color="inherit"
                                    sx={{ color: 'text.secondary' }}
                                    onClick={() => setPickerOpen(true)}
                                    disabled={isSaving}
                                >
                                    Add from Pool
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>

            {/* Problem Picker Dialog */}
            {selectedSessionId && (
                <ProblemPickerDialog
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                    sessionId={selectedSessionId}
                    groupId={group.id}
                    onSuccess={handlePickerSuccess}
                />
            )}

            {/* Schedule (go-live) Dialog */}
            <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon color="primary" />
                    Week {group.groupOrder || '?'} Schedule
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Go live date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText="When this week becomes visible to students. Leave empty to make it visible immediately."
                            fullWidth
                        />
                        <TextField
                            label="Submissions close"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText="When students can no longer submit answers. Leave empty for no end date."
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScheduleOpen(false)} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSaveSchedule} disabled={isSaving}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default SessionGroupCard;
